import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Tabs, Tab, TextField, Switch, FormControlLabel, Button, CircularProgress, Alert, Snackbar } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useI18n, getAcceptLanguageHeader } from '../i18n';
import {
  NOTIFICATION_LANGUAGES as LANGUAGES,
  NOTIFICATION_STATUSES as STATUS_LABELS,
  REWARD_PLACEHOLDERS,
} from '../constants/notificationTemplates';

const getDefaultTemplates = () => {
  const obj = {};
  LANGUAGES.forEach(lang => {
    obj[lang.code] = {};
    STATUS_LABELS.forEach(status => {
      obj[lang.code][status.key] = { text: '', subject: '', enabled: true };
    });
  });
  return obj;
};

export default function EmailTemplatesPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [tab, setTab] = useState(0);
  const [templates, setTemplates] = useState(getDefaultTemplates());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [changedTemplates, setChangedTemplates] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const langCodeToId = React.useRef({});
  const statusKeyToId = React.useRef({});

  useEffect(() => {
    if (!user?.api_token) return;
    setLoading(true);
    setError('');
    fetch('/api/templates', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${user.api_token}`,
        'Accept-Language': getAcceptLanguageHeader(),
      },
    })
      .then(res => {
        if (!res.ok) throw new Error(t('Ошибка загрузки шаблонов'));
        return res.json();
      })
      .then(data => {
        const arr = Array.isArray(data.templates) ? data.templates : data;
        const parsed = getDefaultTemplates();
        const langMap = {};
        const statusMap = {};
        arr.forEach(lang => {
          langMap[lang.language_code] = lang.language_id;
          let statuses = lang.statuses;
          if (typeof statuses === 'string') {
            try {
              statuses = JSON.parse(statuses);
            } catch {
              statuses = {};
            }
          }
          Object.entries(statuses).forEach(([statusKey, statusObj]) => {
            if (!parsed[lang.language_code][statusKey]) return;
            parsed[lang.language_code][statusKey] = {
              text: statusObj.body || '',
              subject: statusObj.subject || '',
              enabled: Boolean(statusObj.enabled),
            };
            statusMap[statusKey] = statusObj.status_id;
          });
        });
        langCodeToId.current = langMap;
        statusKeyToId.current = statusMap;
        setTemplates(parsed);
        setLoading(false);
      })
      .catch(e => {
        setError(e.message || t('Ошибка загрузки шаблонов'));
        setLoading(false);
      });
  }, [user]);

  if (!user || user.role !== 'admin') {
    return (
      <div style={{ color: 'red', margin: '32px 0', fontWeight: 600, fontSize: '1.2rem' }}>
        Доступ запрещён. Только для администратора.
      </div>
    );
  }

  const handleTabChange = (e, newValue) => setTab(newValue);

  const updateChangedTemplates = (langCode, statusKey, body, subject, enabled) => {
    const language_id = langCodeToId.current[langCode];
    const status_id = statusKeyToId.current[statusKey];
    if (!language_id || !status_id) return;
    setChangedTemplates(prev => {
      const idx = prev.findIndex(
        t => t.language_id === language_id && t.status_id === status_id
      );
      const newObj = { language_id, status_id, body, subject, enabled };
      if (idx === -1) {
        return [...prev, newObj];
      } else {
        const arr = [...prev];
        arr[idx] = newObj;
        return arr;
      }
    });
  };

  const handleFieldChange = (lang, status, value, field) => {
    setTemplates(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [status]: {
          ...prev[lang][status],
          [field]: value,
        },
      },
    }));
    const t = templates[lang][status];
    updateChangedTemplates(
      lang,
      status,
      field === 'text' ? value : t.text,
      field === 'subject' ? value : t.subject,
      t.enabled
    );
  };

  const handleSwitchChange = (lang, status, checked) => {
    setTemplates(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [status]: {
          ...prev[lang][status],
          enabled: checked,
        },
      },
    }));
    const t = templates[lang][status];
    updateChangedTemplates(
      lang,
      status,
      t.text,
      t.subject,
      checked
    );
  };

  const handleSave = async () => {
    if (!changedTemplates.length) {
      setSnackbar({ open: true, message: t('Нет изменений для сохранения'), severity: 'info' });
      return;
    }
    try {
      setLoading(true);
      setError('');
      const res = await fetch('/api/templates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.api_token}`,
          'Accept-Language': getAcceptLanguageHeader(),
        },
        body: JSON.stringify(changedTemplates),
      });
      if (!res.ok) throw new Error(t('Ошибка сохранения шаблонов'));
      setSnackbar({ open: true, message: t('Шаблоны успешно сохранены!'), severity: 'success' });
      setChangedTemplates([]);
    } catch (e) {
      setSnackbar({ open: true, message: e.message || t('Ошибка сохранения шаблонов'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const currentLang = LANGUAGES[tab].code;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>{t('Email шаблоны')}</Typography>
      <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 3 }}>
          {LANGUAGES.map((lang, idx) => (
            <Tab key={lang.code} label={lang.label} />
          ))}
        </Tabs>
        <Box>
          {STATUS_LABELS.map((status, idx) => (
            <Box
              key={status.key}
              sx={{
                mb: 2,
                pb: 2,
                borderBottom: idx !== STATUS_LABELS.length - 1 ? '1px solid #eee' : 'none',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 0,
              }}
            >
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  label={status.label}
                  value={templates[currentLang][status.key].text}
                  onChange={e => handleFieldChange(currentLang, status.key, e.target.value, 'text')}
                  fullWidth
                  sx={{ mb: 1 }}
                  multiline
                  minRows={4}
                  helperText={status.key === 'reward' ? `Плейсхолдеры: ${REWARD_PLACEHOLDERS}` : undefined}
                  disabled={!templates[currentLang][status.key].enabled || loading}
                />
                <TextField
                  label="Тема"
                  value={templates[currentLang][status.key].subject}
                  onChange={e => handleFieldChange(currentLang, status.key, e.target.value, 'subject')}
                  fullWidth
                  multiline
                  minRows={1}
                  disabled={!templates[currentLang][status.key].enabled || loading}
                />
              </Box>
              <FormControlLabel
                sx={{ ml: 3, minWidth: 120, flex: '0 0 auto', justifyContent: 'flex-end' }}
                control={
                  <Switch
                    checked={templates[currentLang][status.key].enabled}
                    onChange={e => handleSwitchChange(currentLang, status.key, e.target.checked)}
                    disabled={loading}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#111',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#111',
                      },
                    }}
                  />
                }
                label={templates[currentLang][status.key].enabled ? t('Вкл') : t('Выкл')}
                labelPlacement="start"
              />
            </Box>
          ))}
        </Box>
      </Paper>
      <Button variant="contained" color="primary" onClick={handleSave} sx={{ fontWeight: 600, minWidth: 180 }} disabled={loading}>
        {t('Сохранить')}
      </Button>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
} 